import { useFormContext } from 'react-hook-form';
import { Input } from '@/components/common';
import type { HealthQuoteFormData } from '../../schema';
import { YesNoToggle } from './YesNoToggle';

/**
 * Step 4 of the Health branch: Applicant Annual Income + the three Yes/No
 * eligibility/discount toggles. This is the last data-entry step before
 * Review — "Calculate Premium" (the terminal CTA) stays disabled until every
 * mandatory field across the WHOLE flow validates, not just this step's.
 */
export function AdditionalDetailsStep() {
  const {
    register,
    watch,
    setValue,
    formState: { errors },
  } = useFormContext<HealthQuoteFormData>();

  const preExistingCondition = watch('pre_existing_condition');
  const nriDiscount = watch('nri_discount');
  const bureauScoreDiscount = watch('bureau_score_discount');

  return (
    <div className="space-y-8">
      <div>
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Additional Details</h2>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1 mb-6">
          A few more details to fine-tune the premium.
        </p>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Input
            label="Applicant Annual Income"
            type="number"
            min={0}
            required
            leftIcon={<span className="text-gray-500 dark:text-gray-400">₹</span>}
            error={errors.applicant_annual_income?.message}
            {...register('applicant_annual_income')}
          />
        </div>
      </div>

      <div className="border-t border-gray-100 dark:border-gray-700 pt-6">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <YesNoToggle
            label="Pre-existing Medical Condition"
            required
            value={preExistingCondition}
            onChange={(v) => setValue('pre_existing_condition', v, { shouldValidate: true, shouldDirty: true })}
            error={errors.pre_existing_condition?.message}
          />
          <YesNoToggle
            label="NRI Discount"
            required
            value={nriDiscount}
            onChange={(v) => setValue('nri_discount', v, { shouldValidate: true, shouldDirty: true })}
            error={errors.nri_discount?.message}
          />
          <YesNoToggle
            label="Bureau Score Discount"
            required
            value={bureauScoreDiscount}
            onChange={(v) => setValue('bureau_score_discount', v, { shouldValidate: true, shouldDirty: true })}
            error={errors.bureau_score_discount?.message}
          />
        </div>
      </div>
    </div>
  );
}
